import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Button } from "@mui/material";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import {
  truckFieldConfiguration,
  truckInitialValues,
} from "../../../formConfigs/Fleet/truckFieldConfig";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";
import { fetchLoans } from "../LoanTable/LoanTable";

export const fetchOwnerCompanies = async (token: string | null | undefined) => {
  const response = await axios.get(`${apiVariable}/ownerCompany`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.map((company: any) => ({
    label: company.name,
    id: company.id.toString(),
  }));
};

const TruckPopup = () => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const [prefilledValues, setPrefilledValues] = useState(truckInitialValues);
  const [shouldReset, setShouldReset] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    if (shouldReset) {
      setShouldReset(false);
    }
  }, [shouldReset]);

  const { data: ownerCompanies } = useQuery({
    queryKey: ["ownerCompanies"],
    queryFn: () => fetchOwnerCompanies(token),
    enabled: !!open,
  });

  const { data: loansData } = useQuery({
    queryKey: ["loans"],
    queryFn: () =>
      fetchLoans(token, new Date().toISOString().slice(0, 16), true).then(
        (response) =>
          response.map((loan: any) => ({
            label: `${loan.loanNumber} - ${loan.lenderName} - $${loan.monthlyPayment}`,
            id: loan.id,
          }))
      ),
    enabled: !!open,
  });

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setPrefilledValues(truckInitialValues);
  };
  const handleSnackbarClose = () => setStatus("idle");

  const mutation = useMutation({
    mutationFn: (data: any) =>
      axios.post(`${apiVariable}/truck`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    onSuccess: () => {
      setStatus("success");
      queryClient.invalidateQueries({ queryKey: ["trucksTable"] });
      queryClient.invalidateQueries({ queryKey: ["assets", "Truck"] });
      handleClose();
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response.data || "Failed to submit truck");
    },
  });

  const submitHandler = (data: any) => {
    const finalData = {
      ...data,
      companyOwnerId: getId(data.companyOwnerId),
      loanId: getId(data.loanId),
    };
    mutation.mutate(finalData);
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Add Truck
      </Button>
      <GenericFormPopup
        formFields={truckFieldConfiguration}
        initialValues={prefilledValues}
        title="Add Truck"
        onSubmit={submitHandler}
        open={open}
        onClose={handleClose}
        loading={mutation.isPending}
        shouldReset={shouldReset}
        autocompleteData={{
          ownerCompanies: ownerCompanies,
          loansData: loansData,
        }}
      />
      <StatusSnackbar
        status={status}
        successMessage={"Truck added successfully"}
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default TruckPopup;
